import { PassengerCounts, Traveler, TRAVELER_TYPE, CepId } from '@flysas-tech/book-value-stream-models';
import { DateTime } from 'luxon';

import { assertNotNullish, isDefined, stringToEnum, ValueOf } from '../utils/types';

import { FrequentFlyerValidatorResponse } from './../schemas/api/award-api/travelers';
import { Passenger, PassengerErrors, PassengerFieldError, PAX_TYPE } from './../schemas/sas-domain';

const PHONE_MIN_LENGTH = 7;
const EMAIL_MAX_LENGTH = 256;

export const EMAIL_REGEX = /^\S+@\S+\.\S+$/i;
export const FULL_PHONE_REGEX = /^\+\d{9,}$/;

const FIRST_NAME_REGEX = /^([a-zA-Z0-9.,`´^'_\u0080-\u00ff\s-]{1,30})$/;
const LAST_NAME_REGEX = /^([a-zA-Z0-9.,`´^'_\u0080-\u00ff\s-]{2,30})$/;
const MIDDLE_NAME_REGEX = /^([a-zA-Z0-9.,`´^'_\u0080-\u00ff\s-]{0,30})$/;

export function isEmailValid(value: unknown) {
  if (value === undefined || typeof value !== 'string') return false;
  if (value.length < EMAIL_MAX_LENGTH) return EMAIL_REGEX.test(value);
}

export function getEmailError(email: unknown) {
  if (!email) {
    return PassengerFieldError.EMAIL_REQUIRED;
  } else if (!isEmailValid(email)) {
    return PassengerFieldError.EMAIL_BAD_FORMAT;
  }
}

export function isPhoneValid(value: unknown) {
  if (value === undefined || typeof value !== 'string') return false;
  if (value.startsWith('+') && value.length > PHONE_MIN_LENGTH) return FULL_PHONE_REGEX.test(value);
}

export function getPhoneError(phone: unknown) {
  if (!phone) {
    return PassengerFieldError.PHONE_REQUIRED;
  } else if (!isPhoneValid(phone)) {
    return PassengerFieldError.PHONE_BAD_FORMAT;
  }
}

export type Gender = ValueOf<typeof Gender>;
export const Gender = {
  FEMALE: 'F',
  MALE: 'M',
  NON_BINARY: 'X',
} as const;

export function isGenderValid(value: unknown) {
  return typeof value === 'string' && Object.values(Gender).includes(value as Gender);
}

export function getGenderError(gender: unknown) {
  if (!gender) {
    return PassengerFieldError.GENDER_REQUIRED;
  } else if (!isGenderValid(gender)) {
    return PassengerFieldError.GENDER_BAD_FORMAT;
  }
}

export function getFirstNameError(firstName: unknown) {
  if (typeof firstName !== 'string' || firstName.trim() === '') {
    return PassengerFieldError.FIRST_NAME_REQUIRED;
  }

  if (!FIRST_NAME_REGEX.test(firstName.trim())) {
    return PassengerFieldError.FIRST_NAME_BAD_FORMAT;
  }
}

export function getLastNameError(lastName: unknown) {
  if (typeof lastName !== 'string' || lastName.trim() === '') {
    return PassengerFieldError.LAST_NAME_REQUIRED;
  }

  if (!LAST_NAME_REGEX.test(lastName.trim())) {
    return PassengerFieldError.LAST_NAME_BAD_FORMAT;
  }
}

export function getMiddleNameError(middleName: unknown) {
  if (typeof middleName === 'string' && !MIDDLE_NAME_REGEX.test(middleName)) {
    return PassengerFieldError.MIDDLE_NAME_BAD_FORMAT;
  }
}

export function isValidDateOfBirth(dateString: string): boolean {
  let date = DateTime.fromFormat(dateString, 'yyyy-MM-dd');

  if (!date.isValid) {
    date = DateTime.fromFormat(dateString, 'yyyyMMdd');
  }

  if (!date.isValid) {
    return false;
  }

  const today = DateTime.now().startOf('day');
  const dobDate = date.startOf('day');

  const minDate = today.minus({ years: 120 }).startOf('day');

  return dobDate < today && dobDate > minDate;
}

export function isIsoDateFormat(dateString: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
}

export function getDateOfBirthError(dateOfBirth: unknown, type: PAX_TYPE | TRAVELER_TYPE, travelDate: string) {
  if (!dateOfBirth || typeof dateOfBirth !== 'string') {
    return PassengerFieldError.DATE_OF_BIRTH_REQUIRED;
  } else if (!isValidDateOfBirth(dateOfBirth) || !isIsoDateFormat(dateOfBirth)) {
    return PassengerFieldError.DATE_OF_BIRTH_BAD_FORMAT;
  } else if (!isPassengerAgeInRange(dateOfBirth, type, travelDate)) {
    return PassengerFieldError.DATE_OF_BIRTH_PASSENGER_TYPE_MISSMATCH;
  }
}

export const findErrorByType = (errors: PassengerFieldError[] | undefined, errorTypes: string[]) => {
  return errors?.find(error => errorTypes.includes(error));
};

export function getDateDifference(dateOfBirth: string, travelDate: string) {
  const travelDateTime = DateTime.fromISO(travelDate);
  const birthDateTime = DateTime.fromISO(dateOfBirth);
  return {
    years: travelDateTime.diff(birthDateTime, 'years').years,
    days: travelDateTime.diff(birthDateTime, 'days').days,
  };
}

const TravelerAge = {
  ADULT_MIN_AGE_IN_YEARS: 12,
  ADULT_MAX_AGE_IN_YEARS: 120,
  YOUTH_MIN_AGE_IN_YEARS: 12,
  YOUTH_MAX_AGE_IN_YEARS: 25,
  CHILDREN_MIN_AGE_IN_YEARS: 2,
  CHILDREN_MAX_AGE_IN_YEARS: 11,
  INFANT_MIN_AGE_IN_DAYS: 7,
  INFANT_MAX_AGE_IN_YEARS: 2,
};

export const isPassengerAgeInRange = (
  dateOfBirth: string,
  passengerType: PAX_TYPE | TRAVELER_TYPE,
  travelDate: string
): boolean => {
  if (!dateOfBirth || !travelDate) return false;

  const now = DateTime.local().startOf('day');
  const isInPast = now > DateTime.fromISO(dateOfBirth).startOf('day');

  if (!isInPast) return false;

  const { years, days } = getDateDifference(dateOfBirth, travelDate);

  switch (passengerType) {
    case PAX_TYPE.ADULT:
      return isValidAdultsAge(years);
    case PAX_TYPE.YOUTH:
      return isValidYouthsAge(years);
    case PAX_TYPE.CHILD:
    case PAX_TYPE.UNACCOMPANIED_MINOR:
      return isValidChildrenAge(years);
    case PAX_TYPE.INFANT:
      return isValidInfantsAge(years, days);
    default:
      return false;
  }
};

type PassengerCountsMap = {
  [key: string]: number;
};

type PassengerWithType = { type: PAX_TYPE | TRAVELER_TYPE | string };
export function getPassengerCountPerType(passengers: PassengerWithType[]) {
  const passengerCounts: PassengerCountsMap = {
    [PAX_TYPE.ADULT]: 0,
    [PAX_TYPE.YOUTH]: 0,
    [PAX_TYPE.CHILD]: 0,
    [PAX_TYPE.INFANT]: 0,
    [PAX_TYPE.UNACCOMPANIED_MINOR]: 0,
  };

  passengers?.forEach(passenger => {
    const type = stringToEnum(PAX_TYPE, passenger.type);
    if (type) {
      passengerCounts[type]++;
    }
  });

  return passengerCounts;
}

export type PassengerErrorType = ValueOf<typeof PassengerErrorType>;
export const PassengerErrorType = {
  INVALID_PAX: 'INVALID_PAX',
  INVALID_CEPID: 'INVALID_CEPID',
  AT_LEAST_ONE_PAX: 'AT_LEAST_ONE_PAX',
  MAX_NINE_ADULTS_AND_CHILDREN: 'MAX_NINE_ADULTS_AND_CHILDREN',
  INFANT_EXCEEDS_ADULT: 'INFANT_EXCEEDS_ADULT',
  YOUNG_COUNT_ERROR: 'YOUNG_COUNT_ERROR',
  AT_LEAST_ONE_YOUNG: 'AT_LEAST_ONE_YOUNG',
  MAX_NINE_YOUNG: 'MAX_NINE_YOUNG',
  MAX_NINE_CHILD: 'MAX_NINE_CHILD',
  UMNR_COUNT_ERROR: 'UMNR_COUNT_ERROR',
} as const;

export function validatePassengerCounts(
  passengerCounts: Partial<PassengerCounts>,
  cepId?: CepId
): { isValid: false; errorMsg: PassengerErrorType } | { isValid: true; errorMsg: undefined } {
  const { adults, children, infants, youths } = passengerCounts;
  const adultCount = adults ?? 1;
  const youthCount = youths ?? 0;
  const infantCount = infants ?? 0;
  const childrenCount = children ?? 0;

  const isYouthValid =
    youthCount > 0 && youthCount <= 9 && adultCount === 0 && childrenCount === 0 && infantCount === 0;

  if (cepId === CepId.YOUNG && !isYouthValid) {
    return { isValid: false, errorMsg: PassengerErrorType.YOUNG_COUNT_ERROR };
  }

  if (cepId !== CepId.YOUNG && isYouthValid) {
    return { isValid: false, errorMsg: PassengerErrorType.INVALID_CEPID };
  }

  const isUmnrValid =
    childrenCount > 0 && childrenCount <= 9 && adultCount === 0 && youthCount === 0 && infantCount === 0;

  if (cepId === CepId.UNACCOMPANIED_MINOR && !isUmnrValid) {
    return { isValid: false, errorMsg: PassengerErrorType.UMNR_COUNT_ERROR };
  }

  if (isUmnrValid && cepId !== CepId.UNACCOMPANIED_MINOR) {
    return { isValid: false, errorMsg: PassengerErrorType.INVALID_CEPID };
  }

  if (adultCount + childrenCount > 9) {
    return { isValid: false, errorMsg: PassengerErrorType.MAX_NINE_ADULTS_AND_CHILDREN };
  }

  if (infantCount > adultCount) {
    return { isValid: false, errorMsg: PassengerErrorType.INFANT_EXCEEDS_ADULT };
  }

  return { isValid: true, errorMsg: undefined };
}

export function getBonusProgramError(ffpReponse: FrequentFlyerValidatorResponse) {
  const { validationDetails, accept } = ffpReponse;

  if (!accept) {
    return PassengerFieldError.FFP_INVALID;
  }

  if (validationDetails.numberValid === false) {
    return PassengerFieldError.FFP_NUMBER_INVALID;
  }

  if (validationDetails.accountValid === false) {
    return PassengerFieldError.FFP_ACCOUNT_INVALID;
  }

  // internal means that loyalty does not hit an external API to validate the bonus-program.
  // I.e we do not get any name validations
  if (validationDetails.replyFrom !== 'INTERNAL' && validationDetails.nameMatch === false) {
    return PassengerFieldError.FFP_NAME_MATCH_INVALID;
  }
}

export type PassengerErrorMap = Partial<Record<string, PassengerErrors>>;

type PassengerErrorConditions = {
  passenger: Passenger | Traveler;
  travelDate: string;
  isMainPassenger: boolean;
  isDateOfBirthRequired: boolean;
  isGenderRequired: boolean;
  cepId?: CepId;
};

export function getPassengerErrors({
  passenger,
  travelDate,
  isMainPassenger,
  isDateOfBirthRequired,
  isGenderRequired,
  cepId,
}: PassengerErrorConditions): PassengerErrors {
  const isContactDetailsRequired = isMainPassenger && cepId !== CepId.UNACCOMPANIED_MINOR;
  return {
    firstName: getFirstNameError(passenger.firstName),
    middleName: getMiddleNameError(passenger.middleName),
    lastName: getLastNameError(passenger.lastName),
    gender: isGenderRequired ? getGenderError(passenger.gender) : undefined,
    dateOfBirth: isDateOfBirthRequired
      ? getDateOfBirthError(passenger.dateOfBirth, passenger.type, travelDate)
      : undefined,
    email: isContactDetailsRequired ? getEmailError(passenger.email) : undefined,
    phoneNumber: isContactDetailsRequired ? getPhoneError(passenger.phoneNumber) : undefined,
  };
}

export function validatePassengersFields(
  passengers: Array<Passenger | Traveler>,
  travelDate: string,
  isSecureFlight: boolean,
  cepId?: CepId
) {
  const mainPassenger = passengers
    .sort((a, b) => a.id.localeCompare(b.id))
    .find(
      passenger =>
        passenger.type === PAX_TYPE.ADULT ||
        passenger.type === PAX_TYPE.YOUTH ||
        passenger.type === PAX_TYPE.UNACCOMPANIED_MINOR
    );

  assertNotNullish(mainPassenger, 'Main passenger not found');

  return passengers.reduce(
    (acc, passenger) => ({
      ...acc,
      [passenger.id]: getPassengerErrors({
        passenger,
        travelDate,
        isMainPassenger: passenger.id === mainPassenger.id,
        isDateOfBirthRequired: isSecureFlight || passenger.type !== PAX_TYPE.ADULT,
        isGenderRequired: isSecureFlight || passenger.type === PAX_TYPE.ADULT || passenger.type === PAX_TYPE.YOUTH,
        cepId,
      }),
    }),
    {}
  );
}

export function hasPassengerFieldErrorsForPassenger(passengerFieldErrors: PassengerErrors) {
  return Object.values(passengerFieldErrors).some(isDefined);
}

export function hasPassengerFieldErrors(passengerFieldErrorsMap: PassengerErrorMap) {
  return Object.values(passengerFieldErrorsMap).some(
    passengerFieldErrors => passengerFieldErrors && hasPassengerFieldErrorsForPassenger(passengerFieldErrors)
  );
}

export function getTravelersAge(dateOfBirth: string, travelDate: string) {
  const birthDate = DateTime.fromISO(dateOfBirth);
  const travelDateObj = DateTime.fromISO(travelDate);

  const ageInYears = travelDateObj.diff(birthDate, 'years').years;
  const ageInDays = travelDateObj.diff(birthDate, 'days').days;
  return { ageInYears, ageInDays };
}

export function calculateTravelerTypeBasedOnBirthDate(dateOfBirth: string, travelDate: string) {
  const { ageInYears, ageInDays } = getTravelersAge(dateOfBirth, travelDate);

  if (isValidAdultsAge(ageInYears)) return PAX_TYPE.ADULT;
  if (isValidYouthsAge(ageInYears)) return PAX_TYPE.YOUTH;
  if (isValidChildrenAge(ageInYears)) return PAX_TYPE.CHILD;
  if (isValidInfantsAge(ageInYears, ageInDays)) return PAX_TYPE.INFANT;

  return PAX_TYPE.ADULT;
}

function isValidAdultsAge(years: number) {
  return years < TravelerAge.ADULT_MAX_AGE_IN_YEARS && years >= TravelerAge.ADULT_MIN_AGE_IN_YEARS;
}

function isValidChildrenAge(years: number) {
  return years < TravelerAge.CHILDREN_MAX_AGE_IN_YEARS && years >= TravelerAge.CHILDREN_MIN_AGE_IN_YEARS;
}

function isValidInfantsAge(years: number, days: number) {
  return years < TravelerAge.INFANT_MAX_AGE_IN_YEARS && days >= TravelerAge.INFANT_MIN_AGE_IN_DAYS;
}

export function isValidYouthsAge(years: number) {
  return years >= TravelerAge.YOUTH_MIN_AGE_IN_YEARS && years <= TravelerAge.YOUTH_MAX_AGE_IN_YEARS;
}
